import React from 'react';
import { FileCode } from 'lucide-react';
import { FindingEvidence } from '../../types/finding';

interface FindingCodeSnippetProps {
  evidence: FindingEvidence;
}

/** The file's own lines around a finding, with the cited lines marked. */
const FindingCodeSnippet: React.FC<FindingCodeSnippetProps> = ({ evidence }) => {
  const { snippet, commit_hash } = evidence;
  const lines = snippet.code.split('\n');
  const range =
    snippet.line_end > snippet.line_start ? `${snippet.line_start}-${snippet.line_end}` : `${snippet.line_start}`;

  return (
    <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4 sm:p-6 mb-6">
      <h2 className="text-sm font-semibold text-ui-text-primary mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 pb-3 border-b border-ui-border-primary">
        <FileCode className="w-4 h-4" />
        Code
        <span className="font-mono font-normal text-xs text-ui-text-secondary break-all" title={snippet.file_path}>
          {snippet.file_path}:{range}
        </span>
        {commit_hash && (
          <span className="font-mono font-normal text-xs text-ui-text-tertiary">@ {commit_hash.substring(0, 7)}</span>
        )}
      </h2>

      <div className="overflow-x-auto rounded-lg bg-ui-background-tertiary" data-testid="finding-code-snippet">
        <table className="w-full border-collapse font-mono text-xs leading-5">
          <tbody>
            {lines.map((line, index) => {
              const lineNumber = snippet.code_start_line + index;
              const cited = lineNumber >= snippet.line_start && lineNumber <= snippet.line_end;
              return (
                <tr
                  key={lineNumber}
                  data-cited={cited ? 'true' : undefined}
                  className={cited ? 'bg-brand-primary/10' : undefined}
                >
                  <td
                    className={`select-none text-right align-top pl-3 pr-3 w-px whitespace-nowrap border-l-2 ${
                      cited ? 'border-brand-primary text-ui-text-secondary' : 'border-transparent text-ui-text-tertiary'
                    }`}
                  >
                    {lineNumber}
                  </td>
                  <td className="pr-4 whitespace-pre text-ui-text-primary">{line || ' '}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(snippet.verified === false || snippet.truncated) && (
        <div className="mt-3 space-y-1 text-xs text-ui-text-secondary">
          {snippet.verified === false && (
            <p>Line numbers not confirmed. The code the scan quoted was not found on the highlighted lines.</p>
          )}
          {snippet.truncated && <p>This finding covers more code than is shown here.</p>}
        </div>
      )}
    </div>
  );
};

export default FindingCodeSnippet;
